import { writable, type Updater, type Writable } from "svelte/store";

export interface Serializer<T> {
  serialize: (value: T) => string;
  deserialize: (text: string) => T;
}

export interface StorageBackend {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

interface PersistedOptions<T> {
  serializer?: Serializer<T>;
  storage?: StorageBackend;
}

const jsonSerializer: Serializer<unknown> = {
  serialize: JSON.stringify,
  deserialize: JSON.parse,
};

export function stringSerializer<T extends string>(): Serializer<T> {
  return {
    serialize: (v: T) => v,
    deserialize: (v: string) => v as T,
  };
}

// Cache writable instances by key so two components calling
// `persisted("foo", ...)` get the SAME store and observe each other's
// updates within the same tab. Without this, each call creates a fresh
// writable that only reads localStorage at construction time, so a
// `set` from one component never reaches subscribers in another.
const storeCache = new Map<string, Writable<unknown>>();

export function persisted<T>(key: string, initialValue: T, options?: PersistedOptions<T>): Writable<T> {
  const cached = storeCache.get(key);
  if (cached) return cached as Writable<T>;

  const storage = options?.storage ?? localStorage;
  const serializer = (options?.serializer ?? jsonSerializer) as Serializer<T>;

  function read(): T {
    const stored = storage.getItem(key);
    if (stored === null) return initialValue;
    try {
      return serializer.deserialize(stored);
    } catch {
      return initialValue;
    }
  }

  function write(value: T): void {
    storage.setItem(key, serializer.serialize(value));
  }

  const store = writable<T>(read());
  const { subscribe } = store;

  function set(value: T): void {
    store.set(value);
    write(value);
  }

  function update(fn: Updater<T>): void {
    store.update((current) => {
      const newValue = fn(current);
      write(newValue);
      return newValue;
    });
  }

  const wrapped: Writable<T> = { subscribe, set, update };
  storeCache.set(key, wrapped as Writable<unknown>);
  return wrapped;
}
